import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";

const VersionContext = createContext();

const CURRENT_VERSION_KEY = "app_current_version";
const VERSION_CHECK_INTERVAL = 30000;
const REMIND_LATER_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const VersionProvider = ({ children }) => {
  const [currentVersion, setCurrentVersion] = useState(() => {
    return localStorage.getItem(CURRENT_VERSION_KEY) || null;
  });
  const [latestVersion, setLatestVersion] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isCheckingVersion, setIsCheckingVersion] = useState(true);

  const clearCacheAndReload = useCallback(() => {
    // console.log("[Version] Clearing cache and reloading...");

    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });

      navigator.serviceWorker.addEventListener("message", function handler(event) {
        if (event.data && event.data.type === "CACHE_CLEARED") {
          navigator.serviceWorker.removeEventListener("message", handler);

          if ("caches" in window) {
            caches
              .keys()
              .then((names) => {
                names.forEach((name) => caches.delete(name));
              })
              .then(() => {
                localStorage.setItem(CURRENT_VERSION_KEY, latestVersion);
                window.location.reload(true);
              });
          } else {
            localStorage.setItem(CURRENT_VERSION_KEY, latestVersion);
            window.location.reload(true);
          }
        }
      });

      setTimeout(() => {
        localStorage.setItem(CURRENT_VERSION_KEY, latestVersion);
        window.location.reload(true);
      }, 2000);
    } else {
      if ("caches" in window) {
        caches
          .keys()
          .then((names) => {
            names.forEach((name) => caches.delete(name));
          })
          .then(() => {
            localStorage.setItem(CURRENT_VERSION_KEY, latestVersion);
            window.location.reload(true);
          });
      } else {
        localStorage.setItem(CURRENT_VERSION_KEY, latestVersion);
        window.location.reload(true);
      }
    }
  }, [latestVersion]);

  useEffect(() => {
    // console.log("[Version] Setting up version listener...");

    const versionDocRef = doc(firestore, "versionControl", "cmsNL");

    const unsubscribe = onSnapshot(
      versionDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const newVersion = data.version;

          // console.log("[Version] Latest version from Firestore:", newVersion);
          // console.log("[Version] Current local version:", currentVersion);

          setLatestVersion(newVersion);
          setIsCheckingVersion(false);

          if (!currentVersion) {
            // console.log("[Version] First load, setting current version");
            localStorage.setItem(CURRENT_VERSION_KEY, newVersion);
            setCurrentVersion(newVersion);
          } else if (currentVersion !== newVersion) {
            console.log("[Version] Update detected!");
            setUpdateAvailable(true);
          }
        } else {
          console.warn("[Version] No version document found in Firestore");
          setIsCheckingVersion(false);
        }
      },
      (error) => {
        console.error("[Version] Error listening to version:", error);
        setIsCheckingVersion(false);
      },
    );

    return () => {
      // console.log("[Version] Cleaning up version listener");
      unsubscribe();
    };
  }, [currentVersion]);

  const dismissUpdate = useCallback(() => {
    // console.log("[Version] User dismissed update, will remind in 5 minutes");
    setUpdateAvailable(false);

    // Set timer to show modal again after 5 minutes
    setTimeout(() => {
      // console.log("[Version] Remind later timer expired, showing modal again");
      setUpdateAvailable(true);
    }, REMIND_LATER_INTERVAL);
  }, []);

  const applyUpdate = useCallback(() => {
    clearCacheAndReload();
  }, [clearCacheAndReload]);

  const value = {
    currentVersion,
    latestVersion,
    updateAvailable,
    isCheckingVersion,
    dismissUpdate,
    applyUpdate,
  };

  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
};

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error("useVersion must be used within VersionProvider");
  }
  return context;
};
